import { create } from "zustand"
import { devtools } from "zustand/middleware"
import type { LayoutItem } from "react-grid-layout"
import type { WidgetItem, WidgetPaletteItem } from "@/types/dashboard"
import { DEFAULT_WIDGETS, generateWidgetId } from "@/app/home/constants/dashboardDefaults"

export interface DashboardState {
  widgets: WidgetItem[]
  isEditMode: boolean
  snapshot: WidgetItem[] | null
  isDraggingFromPalette: boolean
  draggedPaletteItem: WidgetPaletteItem | null
}

export interface DashboardActions {
  setWidgets: (widgets: WidgetItem[]) => void
  resetToDefault: () => void
  setEditMode: (isEditMode: boolean) => void
  saveSnapshot: () => void
  restoreSnapshot: () => void
  clearSnapshot: () => void
  addWidget: (widget: Omit<WidgetItem, "id">) => void
  insertWidgetAtRow: (widget: Omit<WidgetItem, "id" | "x" | "y">, y: number) => void
  removeWidget: (widgetId: string) => void
  updateWidgetLayout: (widgetId: string, layout: Partial<LayoutItem>) => void
  updateWidgetOptions: (widgetId: string, options: Record<string, unknown>) => void
  duplicateWidget: (widgetId: string) => void
  setDraggingFromPalette: (isDragging: boolean, item?: WidgetPaletteItem) => void
}

export type DashboardStore = DashboardState & DashboardActions

const initialState: DashboardState = {
  widgets: DEFAULT_WIDGETS,
  isEditMode: false,
  snapshot: null,
  isDraggingFromPalette: false,
  draggedPaletteItem: null,
}

export const useDashboardStore = create<DashboardStore>()(
  devtools(
    (set, get) => ({
      ...initialState,

      setWidgets: (widgets) => {
        set({ widgets }, undefined, "setWidgets")
      },

      resetToDefault: () => {
        set(
          { widgets: DEFAULT_WIDGETS.map((w) => ({ ...w, id: generateWidgetId() })) },
          undefined,
          "resetToDefault",
        )
      },

      setEditMode: (isEditMode) => {
        set({ isEditMode }, undefined, "setEditMode")
      },

      saveSnapshot: () => {
        const { widgets } = get()
        set({ snapshot: structuredClone(widgets) }, undefined, "saveSnapshot")
      },

      restoreSnapshot: () => {
        const { snapshot } = get()
        if (snapshot) {
          set(
            { widgets: snapshot, snapshot: null, isEditMode: false },
            undefined,
            "restoreSnapshot",
          )
        }
      },

      clearSnapshot: () => {
        set({ snapshot: null }, undefined, "clearSnapshot")
      },

      addWidget: (widget) => {
        const newWidget: WidgetItem = {
          ...widget,
          id: generateWidgetId(),
        }
        set(
          (state) => ({
            widgets: [...state.widgets, newWidget],
          }),
          undefined,
          "addWidget",
        )
      },

      // 지정한 y 경계(row boundary)에 위젯을 삽입. y 이후의 모든 위젯은 h만큼 아래로 밀림.
      insertWidgetAtRow: (widget, y) => {
        const newWidget: WidgetItem = {
          ...widget,
          id: generateWidgetId(),
          x: 0,
          y,
        }
        set(
          (state) => ({
            widgets: [
              ...state.widgets.map((w) => (w.y >= y ? { ...w, y: w.y + newWidget.h } : w)),
              newWidget,
            ],
          }),
          undefined,
          "insertWidgetAtRow",
        )
      },

      removeWidget: (widgetId) => {
        set(
          (state) => ({
            widgets: state.widgets.filter((w) => w.id !== widgetId),
          }),
          undefined,
          "removeWidget",
        )
      },

      updateWidgetLayout: (widgetId, layout) => {
        set(
          (state) => ({
            widgets: state.widgets.map((w) =>
              w.id === widgetId
                ? {
                    ...w,
                    x: layout.x ?? w.x,
                    y: layout.y ?? w.y,
                    w: layout.w ?? w.w,
                    h: layout.h ?? w.h,
                  }
                : w,
            ),
          }),
          undefined,
          "updateWidgetLayout",
        )
      },

      updateWidgetOptions: (widgetId, options) => {
        set(
          (state) => ({
            widgets: state.widgets.map((w) =>
              w.id === widgetId ? { ...w, options: { ...w.options, ...options } } : w,
            ),
          }),
          undefined,
          "updateWidgetOptions",
        )
      },

      duplicateWidget: (widgetId) => {
        const { widgets } = get()
        const widget = widgets.find((w) => w.id === widgetId)
        if (!widget) {
          return
        }

        const newWidget: WidgetItem = {
          ...widget,
          id: generateWidgetId(),
          y: widget.y + widget.h,
        }

        set(
          (state) => ({
            widgets: [...state.widgets, newWidget],
          }),
          undefined,
          "duplicateWidget",
        )
      },

      setDraggingFromPalette: (isDragging, item) => {
        set(
          {
            isDraggingFromPalette: isDragging,
            draggedPaletteItem: item ?? null,
          },
          undefined,
          "setDraggingFromPalette",
        )
      },
    }),
    {
      store: "dashboard",
      enabled: process.env.NODE_ENV !== "production",
    },
  ),
)

export const widgetsToLayouts = (widgets: WidgetItem[]): LayoutItem[] =>
  widgets.map((widget) => ({
    i: widget.id,
    x: widget.x,
    y: widget.y,
    w: widget.w,
    h: widget.h,
    minW: widget.minW,
    minH: widget.minH,
    maxW: widget.maxW,
    maxH: widget.maxH,
  }))
